import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  Loader2, KeyRound, Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { getErrorMessage } from "@/lib/errors";

type Status =
  | "CONNECTED"
  | "CONFIGURED_BUT_UNVERIFIED"
  | "DISABLED"
  | "MISSING_SECRET"
  | "DEPRECATED"
  | "FAILED";

interface Integration {
  id: string;
  label: string;
  provider: string;
  capability: string;
  status: Status;
  message: string;
  model_version: string | null;
  cost_unit: string | null;
  kill_switch: string | null;
  kill_switch_enabled: boolean | null;
  config: Record<string, unknown>;
  secrets_present: Record<string, boolean>;
  related_functions: string[];
  latency_ms?: number;
  live_tested: boolean;
  last_success_at?: string | null;
  last_failure_at?: string | null;
  last_failure_message?: string | null;
}

interface HealthResponse {
  checked_at: string;
  environment: string;
  summary: Partial<Record<Status, number>>;
  integrations: Integration[];
}

const STATUS_META: Record<Status, { label: string; icon: typeof CheckCircle2; className: string }> = {
  CONNECTED: { label: "Connected", icon: CheckCircle2, className: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" },
  CONFIGURED_BUT_UNVERIFIED: { label: "Configured, unverified", icon: AlertTriangle, className: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  DEPRECATED: { label: "Deprecated", icon: AlertTriangle, className: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  DISABLED: { label: "Disabled", icon: Ban, className: "text-muted-foreground bg-muted/30 border-border" },
  MISSING_SECRET: { label: "Missing credential", icon: KeyRound, className: "text-destructive bg-destructive/10 border-destructive/30" },
  FAILED: { label: "Failed", icon: XCircle, className: "text-destructive bg-destructive/10 border-destructive/30" },
};

const SUMMARY_ORDER: Status[] = [
  "CONNECTED", "CONFIGURED_BUT_UNVERIFIED", "DEPRECATED", "DISABLED", "MISSING_SECRET", "FAILED",
];

function formatTime(value?: string | null): string {
  if (!value) return "never";
  return new Date(value).toLocaleString();
}

export default function AdminIntegrations() {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: e } = await supabase.functions.invoke("integrations-health-check", {});
      if (e) throw e;
      setData(res as HealthResponse);
    } catch (e: unknown) {
      setError(getErrorMessage(e) ?? "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link>
            </Button>
            <h1 className="font-heading text-2xl font-semibold">Integrations</h1>
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Re-check
          </Button>
        </div>

        {data && (
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {SUMMARY_ORDER.map((s) => (
              <SummaryStat
                key={s}
                label={STATUS_META[s].label}
                value={data.summary?.[s] ?? 0}
                className={STATUS_META[s].className.split(" ")[0]}
              />
            ))}
          </div>
        )}

        {error && (
          <Card className="mb-4 border-destructive/40 bg-destructive/5">
            <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {loading && !data && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Checking integrations…
          </div>
        )}

        <div className="grid gap-3 lg:grid-cols-2">
          {data?.integrations.map((i) => (
            <IntegrationCard key={i.id} integration={i} />
          ))}
        </div>

        {data && (
          <p className="mt-6 text-xs text-muted-foreground">
            Last checked {formatTime(data.checked_at)} · environment: {data.environment} ·
            {" "}{data.integrations.length} dependencies
          </p>
        )}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <Card className="bg-card/95 backdrop-blur-xl">
      <CardContent className="p-4">
        <div className={cn("font-heading text-3xl font-semibold", className)}>{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-right text-xs text-foreground">{value}</span>
    </div>
  );
}

function IntegrationCard({ integration }: { integration: Integration }) {
  const meta = STATUS_META[integration.status] ?? STATUS_META.FAILED;
  const Icon = meta.icon;
  const configEntries = Object.entries(integration.config ?? {});

  return (
    <Card className="bg-card/95 backdrop-blur-xl">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-semibold">{integration.label}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {integration.provider} · {integration.capability}
            </p>
          </div>
          <Badge variant="outline" className={cn("gap-1 border", meta.className)}>
            <Icon className="h-3 w-3" />
            {meta.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0 text-sm">
        <p className="text-muted-foreground">{integration.message}</p>

        <div className="space-y-1 rounded-md border border-border/60 p-2">
          <Row label="Model / version" value={integration.model_version ?? "—"} />
          <Row label="Cost unit" value={integration.cost_unit ?? "—"} />
          <Row label="Verification" value={integration.live_tested ? "Live tested" : "Presence only"} />
          <Row label="Latency" value={integration.latency_ms != null ? `${integration.latency_ms} ms` : "—"} />
          <Row label="Last success" value={formatTime(integration.last_success_at)} />
          <Row
            label="Last failure"
            value={
              integration.last_failure_at
                ? `${formatTime(integration.last_failure_at)}${integration.last_failure_message ? ` — ${integration.last_failure_message}` : ""}`
                : "none recorded"
            }
          />
          <Row
            label="Kill switch"
            value={
              integration.kill_switch
                ? `${integration.kill_switch_enabled === false ? "OFF" : "ON"} · ${integration.kill_switch}`
                : "—"
            }
          />
        </div>

        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Required credentials</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(integration.secrets_present).map(([name, present]) => (
              <Badge
                key={name}
                variant="outline"
                className={cn(
                  "font-mono text-[10px]",
                  present
                    ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400"
                    : "border-destructive/30 bg-destructive/5 text-destructive",
                )}
              >
                {present ? "✓" : "✗"} {name}
              </Badge>
            ))}
            {Object.keys(integration.secrets_present).length === 0 && (
              <span className="text-xs text-muted-foreground">none</span>
            )}
          </div>
        </div>

        {configEntries.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Configuration</div>
            <ul className="space-y-0.5 text-xs text-muted-foreground">
              {configEntries.map(([k, v]) => (
                <li key={k}>
                  <span className="font-mono">{k}</span>: {typeof v === "object" ? JSON.stringify(v) : String(v)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {integration.related_functions.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Edge functions</div>
            <div className="flex flex-wrap gap-1">
              {integration.related_functions.map((fn) => (
                <Badge key={fn} variant="secondary" className="font-mono text-[10px]">{fn}</Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
